import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, Clock, User, Calendar, MapPin } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
// Nous utilisons les classes CSS de ClientDayViewMobile pour cohérence visuelle, 
// mais elles sont surchargées par ClientDashboardMobile.css dans le contexte du dashboard
import '../ClientDayViewMobile.css';

function ClientDashboardDayView({ db, client, currentDate, formatDate, formatTime }) {
    const [appointments, setAppointments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const { theme } = useTheme();
    const isDarkMode = document.documentElement.classList.contains('dark');

    // 1. Récupération des employés (pour les couleurs et noms) - Logique copiée de ClientDayCalendar/ClientDashboard
    const fetchEmployees = useCallback(async () => {
        if (!db) return;
        try {
            const employeesRef = collection(db, 'employees');
            const querySnapshot = await getDocs(employeesRef);
            const fetchedEmployees = querySnapshot.docs.map(doc => ({
                userId: doc.id,
                ...doc.data(),
                color: doc.data().color || '#808080'
            }));

            // Filtrer comme sur le desktop
            const filteredEmployees = fetchedEmployees.filter(employee => {
                if (employee.role === 'beneficiaire') return false;
                const isNotArchived = employee.isArchived !== true;
                const isActive = employee.isActive !== false;
                return isNotArchived && isActive;
            });

            setEmployees(filteredEmployees);
        } catch (error) {
            console.error("Erreur lors de la récupération des employés:", error);
        }
    }, [db]);

    // 2. Récupération des rendez-vous (UNIQUEMENT ceux du client) - Logique copiée de ClientDayCalendar
    const fetchAppointments = useCallback(async () => {
        if (!db || !client.id) return;
        try {
            setLoading(true);
            const startOfDay = new Date(currentDate);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(currentDate);
            endOfDay.setHours(23, 59, 59, 999);

            const appointmentsRef = collection(db, 'appointments');
            const q = query(
                appointmentsRef,
                where('clientId', '==', client.id), // CRITIQUE: Filtrer par client
                where('date', '>=', Timestamp.fromDate(startOfDay)),
                where('date', '<=', Timestamp.fromDate(endOfDay))
            );

            const querySnapshot = await getDocs(q);
            const fetchedAppointments = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                date: doc.data().date?.toDate() || new Date(),
            }));

            // Trier par heure
            fetchedAppointments.sort((a, b) => a.startTime.localeCompare(b.startTime));

            setAppointments(fetchedAppointments);
        } catch (error) {
            console.error("Erreur lors de la récupération des rendez-vous:", error);
        } finally {
            setLoading(false);
        }
    }, [db, client.id, currentDate]);

    useEffect(() => {
        fetchEmployees();
        fetchAppointments();
    }, [fetchEmployees, fetchAppointments]);

    const getEmployeeName = (employeeId) => {
        const employee = employees.find(emp => emp.userId === employeeId);
        if (employee) {
            return employee.name || `${employee.firstName} ${employee.lastName}`;
        }
        return "Intervenant non spécifié";
    };

    const getEmployeeColor = (employeeId) => {
        const employee = employees.find(emp => emp.userId === employeeId);
        return employee?.color || '#808080';
    };

    const calculateDuration = (startTime, endTime) => {
        try {
            const [startHour, startMin] = startTime.split(':').map(Number);
            const [endHour, endMin] = endTime.split(':').map(Number);
            const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            if (hours > 0 && minutes > 0) return `${hours}h${minutes}`;
            if (hours > 0) return `${hours}h`;
            return `${minutes}min`;
        } catch (e) {
            return '';
        }
    };

    // RENDU : Utilise la structure CSS "Mobile" pour s'adapter au dashboard
    return (
        <div className="client-dashboard-day-view-mobile">
            {/* Header simplifié (La navigation est gérée par le composant parent, mais on affiche la date ici pour confirmation) */}
            <div className="client-day-view-mobile-header" style={{ boxShadow: 'none', borderBottom: 'none', paddingLeft: 0, paddingRight: 0 }}>
                <h1 className="text-lg font-bold text-gray-800 dark:text-white">
                    {formatDate(currentDate, 'eee. d MMMM')}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {appointments.length} rendez-vous
                </p>
            </div>

            <div className="space-y-3 mt-4">
                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : appointments.length === 0 ? (
                    <div className="client-day-view-mobile-empty-state">
                        <Calendar size={48} className="mx-auto mb-2 text-gray-300" />
                        <p>Aucun rendez-vous ce jour</p>
                    </div>
                ) : (
                    appointments.map((appointment) => {
                        const employeeColor = appointment.employeeId ? getEmployeeColor(appointment.employeeId) : '#808080';
                        const duration = calculateDuration(appointment.startTime, appointment.endTime);

                        return (
                            <div
                                key={appointment.id}
                                className="client-day-view-mobile-appointment-card"
                                style={{
                                    borderLeft: `4px solid ${employeeColor}`,
                                    backgroundColor: isDarkMode ? '#1f2937' : 'white',
                                    // Force relative positioning for notes
                                    position: 'relative'
                                }}
                            >
                                {appointment.notes && appointment.notes.content && (
                                    <div className="absolute top-2 right-2">
                                        <span className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded-full">
                                            📝
                                        </span>
                                    </div>
                                )}

                                <div className="client-day-view-mobile-appointment-header">
                                    <div className="client-day-view-mobile-appointment-time">
                                        <Clock size={14} className="mr-1" />
                                        <span>{formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}</span>
                                        <span className="ml-2 text-xs font-normal text-gray-500">({duration})</span>
                                    </div>
                                </div>

                                <div className="client-day-view-mobile-appointment-employee mt-2">
                                    <div
                                        className="client-day-view-mobile-employee-color"
                                        style={{ backgroundColor: employeeColor }}
                                    ></div>
                                    <span>
                                        {appointment.employeeId ? getEmployeeName(appointment.employeeId) : "Non assigné"}
                                    </span>
                                </div>

                                <div className="client-day-view-mobile-appointment-details mt-1 pl-6">
                                    <span className="text-xs text-gray-500 block">{appointment.serviceType}</span>
                                </div>

                                {/* Status Badge */}
                                <div className="mt-2 pl-6">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${appointment.status === 'confirmed' || appointment.status === 'assigne'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                            : appointment.status === 'cancelled'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                        }`}>
                                        {appointment.status === 'assigne' ? 'Planifié' :
                                            appointment.status === 'confirmed' ? 'Confirmé' :
                                                appointment.status === 'cancelled' ? 'Annulé' : 'En attente'}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default ClientDashboardDayView;
